/* import __COLOCATED_TEMPLATE__ from './teammate-consultation.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { get, put } from 'embercom/lib/ajax';
import type IntlService from 'embercom/services/intl';

interface TimeoutOption {
  text: string;
  value: number;
}

interface QueueOption {
  text: string;
  value: string;
}

interface TeammateConsultationSettings {
  enabled: boolean;
  timeout_hours: number;
  pause_reply_message: string;
  queue: string;
}

interface Args {
  settings?: TeammateConsultationSettings;
}

export default class TeammateConsultation extends Component<{ Args: Args }> {
  @service declare appService: any;
  @service declare notificationsService: any;
  @service declare intl: IntlService;

  @tracked isEnabled = false;
  @tracked timeoutHours = 24;
  @tracked pauseReplyMessage = '';
  @tracked selectedQueue = 'unassigned';
  @tracked isSaving = false;
  @tracked isLoading = true;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.loadSettings();
  }

  get timeoutOptions(): TimeoutOption[] {
    return [
      { text: this.intl.t('ai-agent.teammate-consultation.timeout.option-1h'), value: 1 },
      { text: this.intl.t('ai-agent.teammate-consultation.timeout.option-2h'), value: 2 },
      { text: this.intl.t('ai-agent.teammate-consultation.timeout.option-4h'), value: 4 },
      { text: this.intl.t('ai-agent.teammate-consultation.timeout.option-8h'), value: 8 },
      { text: this.intl.t('ai-agent.teammate-consultation.timeout.option-12h'), value: 12 },
      { text: this.intl.t('ai-agent.teammate-consultation.timeout.option-24h'), value: 24 },
      { text: this.intl.t('ai-agent.teammate-consultation.timeout.option-48h'), value: 48 },
      { text: this.intl.t('ai-agent.teammate-consultation.timeout.option-72h'), value: 72 },
    ];
  }

  get selectedTimeoutValue(): number {
    return this.timeoutHours;
  }

  get queueOptions(): QueueOption[] {
    return [
      {
        text: this.intl.t('ai-agent.teammate-consultation.queue.unassigned'),
        value: 'unassigned',
      },
      { text: this.intl.t('ai-agent.teammate-consultation.queue.general'), value: 'general' },
      { text: this.intl.t('ai-agent.teammate-consultation.queue.support'), value: 'support' },
      { text: this.intl.t('ai-agent.teammate-consultation.queue.billing'), value: 'billing' },
      {
        text: this.intl.t('ai-agent.teammate-consultation.queue.technical'),
        value: 'technical',
      },
    ];
  }

  get selectedQueueValue(): string {
    return this.selectedQueue;
  }

  get pauseReplyPlaceholder(): string {
    return this.intl.t('ai-agent.teammate-consultation.pause-reply.placeholder');
  }

  async loadSettings(): Promise<void> {
    try {
      let response = (await get(
        `/ember/operator_workflows/fin_setup_profiles/${this.appService.app.id}/teammate_consultation_settings`,
      )) as TeammateConsultationSettings;
      this.isEnabled = response.enabled;
      this.timeoutHours = response.timeout_hours;
      this.pauseReplyMessage = response.pause_reply_message || '';
      this.selectedQueue = response.queue || 'unassigned';
    } catch {
      // Settings not yet configured -- use defaults
    } finally {
      this.isLoading = false;
    }
  }

  @action
  toggleEnabled() {
    this.isEnabled = !this.isEnabled;
  }

  @action
  updateTimeout(value: number) {
    this.timeoutHours = value;
  }

  @action
  updatePauseReply(event: Event) {
    let target = event.target as HTMLTextAreaElement;
    this.pauseReplyMessage = target.value;
  }

  @action
  updateQueue(value: string) {
    this.selectedQueue = value;
  }

  @action
  async saveSettings() {
    this.isSaving = true;
    try {
      await put(
        `/ember/operator_workflows/fin_setup_profiles/${this.appService.app.id}/teammate_consultation_settings`,
        {
          teammate_consultation_settings: {
            enabled: this.isEnabled,
            timeout_hours: this.timeoutHours,
            pause_reply_message: this.pauseReplyMessage,
            queue: this.selectedQueue,
          },
        },
      );
      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.teammate-consultation.save-success'),
      );
    } catch {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.teammate-consultation.save-error'),
      );
    } finally {
      this.isSaving = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::TeammateConsultation': typeof TeammateConsultation;
  }
}
